import React from 'react';
import xhr from 'xhr';
import { API_TOKEN } from 'constants';
import ReadOnlyMember from './read-only-member';
import EditableMember from './editable-member';

class Member extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isEditMode: this.props.isEditMode,
      userId: this.props.id,
      memberId: this.props.memberId,
      email: this.props.email,
      readOnly: this.props.readOnly,
      canAccessAllGroups: this.props.canAccessAllGroups,
      manageMembers: this.props.manageMembers,
      manageBilling: this.props.manageBilling,
      hasLogAccess: this.props.hasLogAccess,
      purgeLogs: this.props.purgeLogs,
      receiveBillingEmails: this.props.receiveBillingEmails,
      receiveUsageEmails: this.props.receiveUsageEmails,
      groupIds: this.props.groupIds,
      errorMessage: null,
      saveButtonText: 'Save Changes',
      cancelButtonText: 'Cancel'
    }

    this.onCheckboxChange        = this.onCheckboxChange.bind(this);
    this.onLogAccessChange       = this.onLogAccessChange.bind(this);
    this.onGroupCheckboxChange   = this.onGroupCheckboxChange.bind(this);
    this.onBoolSelectChange      = this.onBoolSelectChange.bind(this);
    this.onLogAccessSelectChange = this.onLogAccessSelectChange.bind(this);
    this.onEditMemberClick       = this.onEditMemberClick.bind(this);
    this.onEditMemberCancel      = this.onEditMemberCancel.bind(this);
    this.onSaveMemberChanges     = this.onSaveMemberChanges.bind(this);
    this.onDeleteMemberClick     = this.onDeleteMemberClick.bind(this);
  }

  componentDidUpdate() {
    this.props.bindLiveFilter();
  }

  // helper functions
  // *********************************************************************************
  focusFilterIfMembersFiltered() {
    const filterMembers = document.getElementById('filter_members');

    if (filterMembers && filterMembers.value) {
      filterMembers.select();
    }
  };

  // updates the state for a given user
  refreshUser(user) {
    this.setState({
      isEditMode: false,
      readOnly: user.member.read_only,
      canAccessAllGroups: user.member.can_access_all_groups,
      manageMembers: user.member.manage_members,
      manageBilling: user.member.manage_billing,
      purgeLogs: user.member.purge_logs,
      receiveBillingEmails: user.member.receive_billing_emails,
      receiveUsageEmails: user.member.receive_usage_emails,
      groupIds: user.member.group_ids,
      saveButtonText: 'Save Changes',
      cancelButtonText: 'Cancel'
    });
  };

  // form field handlers
  // *********************************************************************************

  // general checkbox handler, when the only thing that needs
  // to happen is switching the boolean state
  onCheckboxChange(key) {
    return (e) => {
      this.setState({
        [key]: e.target.checked
      });
    };
  };

  // responsible for toggling views for log access
  onLogAccessChange(e) {
    const isChecked = e.target.checked;

    if (isChecked) {
      this.setState({
        hasLogAccess: true
      });
    } else {
      this.setState({
        hasLogAccess: false,
        canAccessAllGroups: false,
        purgeLogs: false,
        groupIds: []
      });
    }
  };

  // responsible for handling state changes of selected/unselected groups
  onGroupCheckboxChange(e) {
    const isChecked = e.target.checked,
          groupId = Number(e.target.value);

    let groupIds = this.state.groupIds;

    if (isChecked) {
      groupIds.push(groupId);

      this.setState({
        groupIds: groupIds
      });
    } else {
      this.setState({
        groupIds: _.without(groupIds, groupId)
      });
    }
  };

  // general select handler, when the only thing that needs
  // to happen is setting the state; this specific handler is
  // for select's that have 1/0 (boolean) state values
  onBoolSelectChange(key) {
    return (e) => {
      this.setState({
        [key]: (e.target.value == '1')
      });
    };
  };

  // responsible for handling the "Full" vs "Read-Only" log
  // access state; if "Read-Only", we need to make sure the
  // member cannot purge logs
  onLogAccessSelectChange(e) {
    const readOnly = (e.target.value == '1');

    if (readOnly) {
      this.setState({
        readOnly: true,
        purgeLogs: false
      });
    } else {
      this.setState({
        readOnly: false
      });
    }
  };

  // button handlers
  // *********************************************************************************

  onEditMemberClick() {
    this.setState({ isEditMode: true });
  };

  // when cancelling an edit operation, we need to reset the data
  // back to whatever is stored on the server
  onEditMemberCancel(e) {
    e.preventDefault();

    this.setState({
      cancelButtonText: 'Cancelling...'
    });

    xhr(`/api/v1/users/${this.state.userId}.json`, {
      headers: { 'X-Papertrail-Token': API_TOKEN },
      onSuccess: (user) => {
        this.refreshUser(user);
        this.setState({ errorMessage: null });
        this.focusFilterIfMembersFiltered();
      }
    });
  };

  onSaveMemberChanges(e) {
    e.preventDefault();

    this.setState({
      saveButtonText: 'Saving...'
    });

    const params = {
      user: {
        id: this.state.userId,
        email: this.state.email,
        read_only: this.state.readOnly,
        manage_members: this.state.manageMembers,
        manage_billing: this.state.manageBilling,
        purge_logs: this.state.purgeLogs,
        can_access_all_groups: this.state.canAccessAllGroups,
        group_ids: this.state.groupIds,
        receive_billing_emails: this.state.receiveBillingEmails,
        receive_usage_emails: this.state.receiveUsageEmails
      },
      _method: 'PUT',
    }

    xhr(`/api/v1/users/${this.state.userId}.json`, {
      headers: {
        'X-Papertrail-Token': API_TOKEN,
        'Content-Type': 'application/json'
      },
      method: 'PUT',
      body: params,
      onSuccess: (user) => {
        this.refreshUser(user);
        this.focusFilterIfMembersFiltered();
      },
      onError: (response) => {
        if (response) {
          this.setState({
            errorMessage: response.toString(),
            saveButtonText: 'Save Changes'
          });
        }
      }
    });
  };

  onDeleteMemberClick(e) {
    e.preventDefault();

    if (confirm(`Are you sure you want to delete ${this.state.email}?`)) {
      xhr(`/api/v1/users/${this.state.userId}.json`, {
        headers: {
          'X-Papertrail-Token': API_TOKEN,
          'Content-Type': 'application/json'
        },
        method: 'DELETE',
        body: { id: this.state.userId },
        onSuccess: () => {
          const $member = $(`[data-member=${this.state.memberId}]`);
          $member.fadeOut(() => { $member.remove(); });
        },
        onError: (respnose) => {
          if (response) {
            this.setState({
              errorMessage: response.toString()
            });
          }
        }
      });
    }
  };

  // render
  // *********************************************************************************

  render() {
    if (this.state.isEditMode) {
      return (
        <EditableMember
          key={this.props.id}
          id={this.props.id}
          email={this.props.email}
          memberId={this.props.memberId}
          isEditAllForm={this.props.isEditAllForm}
          showDeleteButton={true}
          showEditButton={false}
          showFormButtons={!this.props.isEditAllForm}
          readOnly={this.state.readOnly}
          receiveBillingEmails={this.state.receiveBillingEmails}
          receiveUsageEmails={this.state.receiveUsageEmails}
          canAccessAllGroups={this.state.canAccessAllGroups}
          manageMembers={this.state.manageMembers}
          manageBilling={this.state.manageBilling}
          purgeLogs={this.state.purgeLogs}
          hasLogAccess={this.state.hasLogAccess}
          groupIds={this.state.groupIds}
          editableNotifications={this.props.editableNotifications}
          groups={this.props.groups}
          sso={this.props.sso}
          paysSomeoneElse={this.props.paysSomeoneElse}
          isCurrentMember={this.props.isCurrentMember}
          onEditMemberCancel={this.onEditMemberCancel}
          onSaveMemberChanges={this.onSaveMemberChanges}
          onDeleteMemberClick={this.onDeleteMemberClick}
          onCheckboxChange={this.onCheckboxChange}
          onLogAccessChange={this.onLogAccessChange}
          onBoolSelectChange={this.onBoolSelectChange}
          onLogAccessSelectChange={this.onLogAccessSelectChange}
          onGroupCheckboxChange={this.onGroupCheckboxChange}
          errorMessage={this.state.errorMessage}
          saveButtonText={this.state.saveButtonText}
          cancelButtonText={this.state.cancelButtonText}
        />
      );
    } else {
      return (
        <ReadOnlyMember
          key={this.props.id}
          id={this.props.id}
          email={this.props.email}
          memberId={this.props.memberId}
          showDeleteButton={false}
          showEditButton={true}
          showFormButtons={false}
          readOnly={this.state.readOnly}
          receiveBillingEmails={this.state.receiveBillingEmails}
          receiveUsageEmails={this.state.receiveUsageEmails}
          canAccessAllGroups={this.state.canAccessAllGroups}
          manageMembers={this.state.manageMembers}
          manageBilling={this.state.manageBilling}
          purgeLogs={this.state.purgeLogs}
          hasLogAccess={this.state.hasLogAccess}
          groupIds={this.state.groupIds}
          groups={this.props.groups}
          sso={this.props.sso}
          isCurrentMember={this.props.isCurrentMember}
          onEditMemberClick={this.onEditMemberClick}
        />
      );
    }
  }
};

export default Member;
